import React, { useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isAfter,
  isBefore,
  addMonths,
  subMonths,
  getDay,
  isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface CalendarProps {
  mode: "single" | "range";
  selectedDate?: Date | null;
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  onDayClick: (day: Date) => void;
  maxDate?: Date;
}

const Calendar: React.FC<CalendarProps> = ({
  mode,
  selectedDate,
  rangeStart,
  rangeEnd,
  onDayClick,
  maxDate,
}) => {
  const initialMonth =
    (mode === "single" ? selectedDate : rangeStart) ?? new Date();
  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);
  const [hoverDay, setHoverDay] = useState<Date | null>(null);

  const monthStart = startOfMonth(viewMonth);
  const calDays = eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(endOfMonth(viewMonth)),
  });

  // Effective range end: confirmed end, or hover preview
  const effectiveEnd =
    rangeEnd ??
    (mode === "range" && !rangeEnd && rangeStart && hoverDay && isAfter(hoverDay, rangeStart)
      ? hoverDay
      : null);

  // ── Predicates ──────────────────────────────────────────────
  const isDisabled = (d: Date) => !!(maxDate && isAfter(d, maxDate));

  const isStart = (d: Date) =>
    mode === "range" && !!(rangeStart && isSameDay(d, rangeStart));

  const isEnd = (d: Date) =>
    mode === "range" && !!(rangeEnd && isSameDay(d, rangeEnd));

  const isHoverEnd = (d: Date) =>
    mode === "range" && !rangeEnd && !!(hoverDay && isSameDay(d, hoverDay)) && !!(effectiveEnd);

  const isBetween = (d: Date) => {
    if (!rangeStart || !effectiveEnd) return false;
    return isAfter(d, rangeStart) && isBefore(d, effectiveEnd);
  };

  // A day is "in range" if it's start, end, hover-end, or strictly between
  const inRange = (d: Date) =>
    isStart(d) || isEnd(d) || isHoverEnd(d) || isBetween(d);

  // Strip occupies the LEFT half of the cell (track coming from the left)
  const showLeftStrip = (d: Date) =>
    inRange(d) && !isStart(d) && getDay(d) !== 0;

  // Strip occupies the RIGHT half of the cell (track going to the right)
  const showRightStrip = (d: Date) =>
    inRange(d) && !isEnd(d) && !isHoverEnd(d) && getDay(d) !== 6;

  // ── Circle colour ────────────────────────────────────────────
  const circleClass = (d: Date): string => {
    if (mode === "single" && selectedDate && isSameDay(d, selectedDate))
      return "bg-primary text-white";
    if (isStart(d) || isEnd(d))
      return "bg-primary text-white";
    if (isHoverEnd(d))
      return "bg-primary/40 text-primary";
    if (isToday(d))
      return "ring-1 ring-primary text-primary font-semibold";
    return "";
  };

  // ── Month nav ────────────────────────────────────────────────
  const canGoNext = !maxDate || !isSameMonth(viewMonth, maxDate);

  return (
    <div className="select-none w-64">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          type="button"
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
          className="p-1 rounded hover:bg-gray-100 text-gray-600 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-gray-800">
          {format(viewMonth, "MMMM yyyy")}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          disabled={!canGoNext}
          className="p-1 rounded hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAY_LABELS.map((l) => (
          <div
            key={l}
            className="text-center text-xs font-medium text-gray-400 py-1"
          >
            {l}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7">
        {calDays.map((day) => {
          const disabled = isDisabled(day);
          const outOfMonth = !isSameMonth(day, viewMonth);
          const circle = circleClass(day);

          return (
            <div
              key={day.toISOString()}
              className="relative h-8 flex items-center justify-center"
              onMouseEnter={() => !disabled && setHoverDay(day)}
              onMouseLeave={() => setHoverDay(null)}
            >
              {/* Range strip — left half */}
              {!outOfMonth && showLeftStrip(day) && (
                <div className="absolute inset-y-1 left-0 right-1/2 bg-primary/15 pointer-events-none" />
              )}
              {/* Range strip — right half */}
              {!outOfMonth && showRightStrip(day) && (
                <div className="absolute inset-y-1 left-1/2 right-0 bg-primary/15 pointer-events-none" />
              )}

              {/* Day button */}
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onDayClick(day)}
                className={[
                  "relative z-10 w-8 h-8 flex items-center justify-center rounded-full text-xs font-medium transition-colors",
                  outOfMonth
                    ? "text-gray-300 cursor-default"
                    : disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : circle
                    ? circle
                    : "text-gray-800 hover:bg-gray-100 cursor-pointer",
                ].join(" ")}
              >
                {format(day, "d")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
